import { useEffect, useRef } from 'react';

export interface InfiniteSentinelProps {
  /** Called when the sentinel scrolls into view. The caller owns "am I already loading?" and
   * "is there a next page?" — see `disabled`. */
  onReached: () => void;
  /** When true the observer is not created at all, so a screen that is already loading, has no
   * next page, or has stopped automatic loading after a failure costs nothing here. */
  disabled?: boolean;
  /** How far below the scroller's bottom edge counts as "reached", in CSS pixels. One screen of
   * rows by default, so the next page is usually there before the reader arrives at the end of
   * this one. */
  rootMargin?: number;
}

/**
 * MVP-451. The element that actually scrolls this sentinel, or `null` for the viewport.
 *
 * The trap is clipping, not arithmetic. With the default viewport root, a target that has scrolled
 * out of an intervening `overflow-y: auto` box is clipped to an empty rect and simply is not
 * intersecting — so a sentinel sitting in that box's scroll content, below its visible bottom,
 * cannot be seen no matter how large `rootMargin` is. Making that same box the root inverts it:
 * `rootMargin` now expands the rect of the very element doing the clipping, and the sentinel below
 * its bottom edge is compared against the expanded rect instead of being clipped away.
 *
 * Measured in a 375x812 phone frame on the guest page, whose results panel is exactly such a box:
 * the panel was 348px tall and its bottom edge sat ON the viewport's, so both roots describe the
 * identical region — and yet the shipped version asked for the next page with 56px of scroll left,
 * under one 60px row. The look-ahead was 400px on paper and 0px in practice, and every page arrived
 * as a visible stall at the end of the list instead of quietly ahead of the reader.
 *
 * Resolving the root here rather than taking it as a prop keeps all four call sites unchanged and
 * fixes any list that is later moved inside a scroll box without anyone remembering this exists. A
 * list that scrolls with the page has no such ancestor and gets `null`, which is what it had before.
 */
function nearestScrollParent(el: Element): Element | null {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const { overflowY } = getComputedStyle(p);
    if (overflowY === 'auto' || overflowY === 'scroll') return p;
  }
  return null;
}

/**
 * MVP-450. The one way every list in this app asks for its next page.
 *
 * `VirtualGrid`/`VirtualList` already own this for the two screens whose rows are windowed: their
 * `onEndReached` fires off a row index they are computing anyway. Every other list here is a plain
 * `<ul>` — the admin screens, the guest browse list, the singer picker — and the alternative was
 * four copies of the same `IntersectionObserver` in four feature folders, which is four places for
 * the observer to be created on every render, or torn down and never rebuilt.
 *
 * It renders an empty element rather than wrapping the list, because a component that owns the
 * list owns its markup too, and these five lists have five different row shapes.
 *
 * MVP-230's rule applies wherever this is used, and it is the caller's half: a page that FAILED
 * must stop automatic loading and wait for an explicit Retry. Nobody decides to try again in an
 * infinite scroll, so without that a backend that is down turns the end of a list into a request
 * loop that re-fires on every scroll nudge. Pass `disabled` while an error is unacknowledged.
 *
 * DESIGN §9.2 `InfiniteSentinel`.
 */
export function InfiniteSentinel({ onReached, disabled = false, rootMargin = 400 }: InfiniteSentinelProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  // Ref-read for the same reason `VirtualGrid` does it: an inline arrow from the caller must not
  // tear down and rebuild the observer on every render.
  const reachedRef = useRef(onReached);
  reachedRef.current = onReached;

  useEffect(() => {
    if (disabled) return;
    const el = ref.current;
    // `IntersectionObserver` is absent in this repo's node test environment and in any browser old
    // enough to matter; a list that cannot observe simply never auto-loads, which is the same
    // state as "there is no next page" rather than a crash.
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) reachedRef.current();
      },
      { root: nearestScrollParent(el), rootMargin: `0px 0px ${rootMargin}px 0px` }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [disabled, rootMargin]);

  // `aria-hidden`: this element exists to be seen by an observer, not by a reader. What a screen
  // reader is owed at the end of a list is the loading row or the Retry the caller renders next
  // to it, both of which say something true.
  return <div ref={ref} className="infinite-sentinel" aria-hidden="true" />;
}
